import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Dokument, DokumentEntityTyp, DokumentTyp, DOKUMENT_TYP } from '../types';
import dokumentService from '../services/dokumentService';
import authService from '../services/authService';
import { Card, Button, Select, Spinner, Badge, Icon, Modal } from './ui';
import { ankunft, markiereNeu } from '../utils/ankunft';

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.docx,.xlsx,.doc,.xls';

// Im Browser ohne externen Viewer darstellbar: Bilder und PDF. Office-Dateien → nur Download.
const istVorschaubar = (contentType: string): boolean =>
  contentType.startsWith('image/') || contentType === 'application/pdf';

const TYP_LABEL: Record<DokumentTyp, string> = DOKUMENT_TYP.reduce(
  (acc, t) => ({ ...acc, [t.value]: t.label }), {} as Record<DokumentTyp, string>,
);

// Farbliche Akzentuierung des Datei-Icons nach Typ.
const fileTone = (contentType: string): string => {
  if (contentType === 'application/pdf') return 'bg-red-50 text-red-600';
  if (contentType.startsWith('image/')) return 'bg-emerald-50 text-emerald-600';
  if (contentType.includes('word') || contentType === 'application/msword') return 'bg-blue-50 text-blue-600';
  if (contentType.includes('sheet') || contentType.includes('excel')) return 'bg-green-50 text-green-600';
  return 'bg-gray-100 text-gray-500';
};

// "rechnung.pdf" -> { base: "rechnung", ext: "pdf" }
const splitName = (name: string): { base: string; ext: string } => {
  const i = name.lastIndexOf('.');
  return i > 0 ? { base: name.slice(0, i), ext: name.slice(i + 1) } : { base: name, ext: '' };
};

// ISO-Zeitstempel -> "24.06.2026, 14:30".
const fmtZeit = (iso?: string): string => {
  if (!iso) return '';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const fmtGroesse = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** 40×40-Kachel: Bild-Thumbnail (lazy als Auth-Blob geladen) bzw. farbiges Datei-Icon. Klick öffnet/lädt. */
const DokumentKachel: React.FC<{ dokument: Dokument; onOpen: () => void }> = ({ dokument, onOpen }) => {
  const isImage = dokument.contentType.startsWith('image/');
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage) return;
    let objUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const u = await dokumentService.fetchBlobUrl(dokument.id, dokument.contentType);
        objUrl = u;
        if (cancelled) { window.URL.revokeObjectURL(u); return; }
        setUrl(u);
      } catch { /* Fallback: Icon */ }
    })();
    return () => { cancelled = true; if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [dokument.id, dokument.contentType, isImage]);

  return (
    <button
      type="button"
      onClick={onOpen}
      title={istVorschaubar(dokument.contentType) ? 'Vorschau' : 'Herunterladen'}
      className={`grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg ring-1 ring-black/5 transition-transform hover:scale-105 ${url ? 'bg-gray-100' : fileTone(dokument.contentType)}`}
    >
      {isImage && url
        ? <img src={url} alt="" className="h-full w-full object-cover" />
        : <Icon.Doc className="h-5 w-5" />}
    </button>
  );
};

/** Inline-Vorschau (Bild/PDF) in einem Modal – lädt die Datei als Auth-Blob und zeigt sie ohne Download. */
const DokumentVorschau: React.FC<{ dokument: Dokument; onClose: () => void; onDownload: () => void }> = ({ dokument, onClose, onDownload }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objUrl: string | null = null;
    let cancelled = false;
    (async () => {
      try {
        const u = await dokumentService.fetchBlobUrl(dokument.id, dokument.contentType);
        objUrl = u;
        if (cancelled) { window.URL.revokeObjectURL(u); return; }
        setUrl(u);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; if (objUrl) window.URL.revokeObjectURL(objUrl); };
  }, [dokument.id, dokument.contentType]);

  const isImage = dokument.contentType.startsWith('image/');

  return (
    <Modal title={dokument.dateiname} onClose={onClose} maxWidth="max-w-4xl">
      {error ? (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-500">Vorschau konnte nicht geladen werden.</p>
          <Button className="mt-4" variant="secondary" icon={<Icon.Download className="h-4 w-4" />} onClick={onDownload}>Herunterladen</Button>
        </div>
      ) : !url ? (
        <div className="flex h-[60vh] items-center justify-center text-brand-600"><Spinner className="h-8 w-8" /></div>
      ) : isImage ? (
        <div className="flex max-h-[75vh] items-center justify-center">
          <img src={url} alt={dokument.dateiname} className="max-h-[75vh] w-auto rounded-lg object-contain" />
        </div>
      ) : (
        <iframe src={url} title={dokument.dateiname} className="h-[75vh] w-full rounded-lg border border-gray-200" />
      )}
    </Modal>
  );
};

/** Dezenter, runder Icon-Button für Zeilen-Aktionen. */
const RowIcon: React.FC<{ title: string; onClick: () => void; danger?: boolean; children: React.ReactNode }> = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`rounded-lg p-1.5 transition-colors ${danger ? 'text-gray-400 hover:bg-red-50 hover:text-red-600' : 'text-gray-400 hover:bg-gray-200/70 hover:text-brand-700'}`}
  >
    {children}
  </button>
);

/**
 * Dokumente-Sektion für Detailseiten (Grab/Kunde/Vertrag): Liste der Anhänge mit Vorschau-Kachel,
 * Typ, Größe, Uploader + Datum. Jeder Mitarbeiter kann Dateien (PDF/Bild/Office, max. 20 MB)
 * hochladen und herunterladen; umbenennen/umklassifizieren und löschen darf nur der Uploader
 * (app-seitig erzwungen → 403).
 */
const DokumentSektion: React.FC<{ entityType: DokumentEntityTyp; entityId: number }> = ({ entityType, entityId }) => {
  const currentUserId = authService.getCurrentUser()?.id;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [dokumente, setDokumente] = useState<Dokument[]>([]);
  const [loading, setLoading] = useState(true);
  const [typ, setTyp] = useState<DokumentTyp>('SONSTIGES');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [vorschau, setVorschau] = useState<Dokument | null>(null);

  // Inline-Bearbeitung (Umbenennen + Typ)
  const [editId, setEditId] = useState<number | null>(null);
  const [editBase, setEditBase] = useState('');
  const [editExt, setEditExt] = useState('');
  const [editTyp, setEditTyp] = useState<DokumentTyp>('SONSTIGES');
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDokumente(await dokumentService.getForEntity(entityType, entityId));
    } catch {
      /* leere Liste anzeigen */
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const neu = await dokumentService.upload(entityType, entityId, file, typ);
      markiereNeu(`dokument-${neu.id}`);
      await load();
      toast.success('Dokument hochgeladen');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Dokument konnte nicht hochgeladen werden');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = ''; // Reset, damit dieselbe Datei erneut wählbar ist
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const download = async (d: Dokument) => {
    try {
      await dokumentService.download(d.id, d.dateiname);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Dokument konnte nicht heruntergeladen werden');
    }
  };

  const remove = async (d: Dokument) => {
    if (!window.confirm(`Dokument „${d.dateiname}“ löschen?`)) return;
    try {
      await dokumentService.delete(d.id);
      toast.success('Dokument gelöscht');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Dokument konnte nicht gelöscht werden');
    }
  };

  const startEdit = (d: Dokument) => {
    const { base, ext } = splitName(d.dateiname);
    setEditId(d.id);
    setEditBase(base);
    setEditExt(ext);
    setEditTyp(d.dokumentTyp);
  };
  const cancelEdit = () => { setEditId(null); setEditBase(''); setEditExt(''); };

  const saveEdit = async () => {
    const base = editBase.trim();
    if (!base || editId == null) return;
    const name = editExt ? `${base}.${editExt}` : base;
    setSavingEdit(true);
    try {
      await dokumentService.update(editId, name, editTyp);
      markiereNeu(`dokument-${editId}`);
      cancelEdit();
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Dokument konnte nicht gespeichert werden');
    } finally {
      setSavingEdit(false);
    }
  };

  const openDokument = (d: Dokument) => (istVorschaubar(d.contentType) ? setVorschau(d) : download(d));

  return (
    <Card className="p-6">
      <h3 className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-900">
        <span className="text-gray-400"><Icon.Doc className="h-4 w-4" /></span>
        Dokumente{dokumente.length > 0 ? ` (${dokumente.length})` : ''}
      </h3>

      {/* Upload */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="sm:w-44">
          <label className="mb-1 block text-xs font-medium text-gray-600">Dokumenttyp</label>
          <Select value={typ} onChange={e => setTyp(e.target.value as DokumentTyp)}>
            {DOKUMENT_TYP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          disabled={uploading}
          className={`flex flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-4 transition-colors disabled:opacity-60 ${
            dragOver ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-500 hover:border-brand-400 hover:bg-gray-50 hover:text-brand-700'
          }`}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            {uploading
              ? <><Spinner className="h-4 w-4 text-brand-600" /> Lädt hoch…</>
              : <><Icon.Upload className="h-4 w-4" /> Datei wählen oder hierher ziehen</>}
          </span>
          <span className="text-xs text-gray-400">PDF, JPG, PNG, Word, Excel · max. 20 MB</span>
        </button>
        <input ref={fileInputRef} type="file" accept={ACCEPT} onChange={handleInputChange} className="hidden" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner className="h-4 w-4 text-brand-600" /> Lade Dokumente…</div>
      ) : dokumente.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 py-8 text-center text-sm text-gray-400">
          <p>Noch keine Dokumente. Verträge, Fotos und Schriftverkehr lassen sich hier ablegen.</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="mt-2 rounded-md px-2 py-0.5 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50 disabled:opacity-60"
          >
            Datei hochladen
          </button>
        </div>
      ) : (
        <ul className="space-y-1">
          {dokumente.map(d => {
            const eigener = currentUserId != null && d.userId === currentUserId;
            const vorschaubar = istVorschaubar(d.contentType);
            const editing = editId === d.id;
            const a = ankunft(`dokument-${d.id}`);
            return (
              <li key={d.id} ref={a.ref} className={`flex items-center gap-3 rounded-xl px-2 py-2 transition-colors hover:bg-gray-50 ${a.className}`}>
                <DokumentKachel dokument={d} onOpen={() => openDokument(d)} />

                {editing ? (
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <div className="flex items-center overflow-hidden rounded-lg border border-gray-300 bg-white focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
                      <input
                        autoFocus
                        value={editBase}
                        onChange={e => setEditBase(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                        className="min-w-0 flex-1 bg-transparent px-3 py-1.5 text-sm focus:outline-none"
                      />
                      {editExt && <span className="shrink-0 pr-3 text-xs text-gray-400">.{editExt}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={editTyp} onChange={e => setEditTyp(e.target.value as DokumentTyp)} className="min-w-0 flex-1 sm:flex-none sm:w-40">
                        {DOKUMENT_TYP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </Select>
                      <div className="ml-auto flex shrink-0 gap-1">
                        <Button size="sm" loading={savingEdit} disabled={!editBase.trim()} onClick={saveEdit}>Speichern</Button>
                        <Button size="sm" variant="secondary" onClick={cancelEdit}>Abbrechen</Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <button
                          type="button"
                          onClick={() => openDokument(d)}
                          title={vorschaubar ? 'Vorschau' : 'Herunterladen'}
                          className="truncate text-left text-sm font-medium text-gray-900 hover:text-brand-700 hover:underline"
                        >
                          {d.dateiname}
                        </button>
                        <Badge className="bg-brand-50 text-brand-700">{TYP_LABEL[d.dokumentTyp]}</Badge>
                      </div>
                      <p className="text-xs text-gray-400">
                        {fmtGroesse(d.groesseBytes)} · {d.autorName} · {fmtZeit(d.createdAt)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5">
                      {vorschaubar && <RowIcon title="Vorschau" onClick={() => setVorschau(d)}><Icon.Eye className="h-4 w-4" /></RowIcon>}
                      <RowIcon title="Herunterladen" onClick={() => download(d)}><Icon.Download className="h-4 w-4" /></RowIcon>
                      {eigener && <RowIcon title="Umbenennen" onClick={() => startEdit(d)}><Icon.Edit className="h-4 w-4" /></RowIcon>}
                      {eigener && <RowIcon title="Löschen" danger onClick={() => remove(d)}><Icon.Trash className="h-4 w-4" /></RowIcon>}
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {vorschau && (
        <DokumentVorschau
          dokument={vorschau}
          onClose={() => setVorschau(null)}
          onDownload={() => download(vorschau)}
        />
      )}
    </Card>
  );
};

export default DokumentSektion;
